import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";

import {
  GetFacilityPermissions,
  GetFacilityRoles,
  IFacilityRole,
  IPermissionDetails,
  IPermissionDetailsList,
  PostFacilityAdmin,
  PutFacilityAdminPermissions,
} from "api/rpc/2024-04/facilityAdmin/client/admin/admin";

import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import Page from "components/page/Page";
import { StatusCode } from "api/protocols";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { useTranslation } from "react-i18next";
import { ButtonNew } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { errorMessage } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import { LocaleCurrency } from "helpers/Locale";
import DataTable from "components/dataTable/DataTable";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import PermissionEditor from "./permissions/PermissionEditor";

interface IStaffAccountInputField {
  data: string;
  isDirty: boolean;
  isValid: boolean;
}

interface IAdminState {
  first_name: IStaffAccountInputField;
  last_name: IStaffAccountInputField;
  email: IStaffAccountInputField;
  password: IStaffAccountInputField;
  username: IStaffAccountInputField;
  roles: Array<{ id: number; title: string; wage?: string }>;
  state?: string;
  quick_code?: string;
}

export default function FacilityStaffDetailsEdit() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { facilityStore, authStore } = useAppSelector(store => store);

  // States
  const [staffState, setStaffState] = useState<IAdminState>(undefined);
  const [roleState, setRoleState] = useState<IFacilityRole[]>(null);
  const [accessMode, setAccessMode] = useState<"role" | "custom">("role");
  const [permissionCatalog, setPermissionCatalog] = useState<IPermissionDetailsList>(null);
  const [customPermissions, setCustomPermissions] = useState<Record<string, boolean>>({});
  const { state: roleModal, updateModal: updateRoleModal, closeModal: closeRoleModal } = useModal<{ selectedRoles: Array<{ id: number; title: string; wage?: string }> }>({ selectedRoles: [] });

  const quickCode = staffState?.quick_code ?? "";

  const selectedRoleHasKioskView = useMemo(() => {
    let hasKioskView = false;
    if (staffState?.roles) {
      staffState?.roles?.forEach(role => {
        if (!hasKioskView) {
          const selectedRole = roleState?.find(r => r?.id === role?.id);
          hasKioskView = selectedRole?.permissions?.some(p => p?.key === "kiosk_view" && !!p?.pivot?.boolean_value) ?? false;
        }
      });
    }
    return hasKioskView;
  }, [staffState?.roles, roleState]);

  const kioskViewEnabled = accessMode === "role" ? selectedRoleHasKioskView : Boolean(customPermissions.kiosk_view);
  const pinMissing = kioskViewEnabled && !quickCode;

  useEffect(() => {
    void loadFacilityRoles();
    void loadPermissionCatalog();
  }, []);

  async function loadFacilityRoles() {
    const roleRes = await GetFacilityRoles({ permissions: true }, true);
    if (roleRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading facility roles")));
      return;
    }

    setRoleState(roleRes.data);
  }

  async function loadPermissionCatalog() {
    const permissionRes = await GetFacilityPermissions({}, false);
    if (permissionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error retrieving permissions")));
      return;
    }

    setPermissionCatalog(permissionRes.data);
  }

  function permissionPayload() {
    const payload: Record<string, boolean> = {};
    if (!permissionCatalog) {
      return payload;
    }

    Object.values(permissionCatalog).forEach(groups => {
      Object.values(groups).forEach(permissionList => {
        permissionList.forEach(permission => {
          payload[permission.key] = Boolean(customPermissions[permission.key]);
        });
      });
    });

    return payload;
  }

  async function createUser() {
    if (quickCode && !/^\d+$/.test(quickCode)) {
      dispatch(showError(t("PIN must be a number.")));
      return;
    }

    if (pinMissing) {
      dispatch(showError(t("PIN is required for kiosk view.")));
      return;
    }

    if (accessMode === "role" && (!staffState?.roles || staffState.roles.length === 0)) {
      dispatch(showError(t("Select a role, or choose custom permissions.")));
      return;
    }

    if (accessMode === "custom" && !permissionCatalog) {
      dispatch(showError(t("Error retrieving permissions")));
      return;
    }

    const params = {
      first_name: staffState.first_name,
      last_name: staffState.last_name,
      email: staffState?.email,
      username: staffState?.username,
      password: staffState?.password.data,
      quick_code: quickCode ? Number(quickCode) : undefined,
      roles: accessMode === "role" ? staffState?.roles : [],
    };

    const updateRes = await PostFacilityAdmin(params, true);

    if (updateRes.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(updateRes, t("Error creating user"))));
      return;
    }

    if (accessMode === "custom") {
      const createdUser = updateRes.data as { id?: number };
      if (!createdUser?.id) {
        dispatch(showError(t("Staff account was created, but permissions could not be saved.")));
        history.push("/admin/settings/staff/");
        return;
      }

      const permissionRes = await PutFacilityAdminPermissions(
        {
          user_id: createdUser?.id,
          ...permissionPayload(),
        } as Parameters<typeof PutFacilityAdminPermissions>[0],
        true,
      );

      if (permissionRes.status !== StatusCode.OK) {
        dispatch(showError(t("Staff account was created, but permissions could not be saved.")));
        history.push("/admin/settings/staff/");
        return;
      }
    }

    dispatch(showSuccess(t("User succesfully created")));
    history.push("/admin/settings/staff/");
  }

  // Handlers
  function handleInputChange(event: any) {
    const { id, value } = event.target;
    if (id === "password") {
      const isValid = value !== "" && value != null && value.length >= 5;
      setStaffState(prevState => ({ ...prevState, [id]: { data: value, isDirty: true, isValid } }));
    } else {
      setStaffState(prevState => ({ ...prevState, [id]: value }));
    }
  }

  function handleSelectNewRole(role: Partial<IFacilityRole>) {
    let updatedRoles = roleModal?.selectedRoles
      ? [...roleModal.selectedRoles]
      : [];
    const foundRole = updatedRoles?.find(updatedRole => updatedRole?.id === role?.id);
    if (foundRole) {
      updatedRoles = updatedRoles?.filter(updatedRole => updatedRole?.id !== role?.id);
    } else {
      updatedRoles.push({ id: role?.id, title: role?.title, wage: role?.wage });
    }
    updateRoleModal({ selectedRoles: updatedRoles });
  }

  function handleSelectRole(role: Partial<IFacilityRole>) {
    let updatedRoles = staffState?.roles
      ? [...staffState.roles]
      : [];
    const foundRole = updatedRoles?.find(updatedRole => updatedRole?.id === role?.id);
    if (foundRole) {
      updatedRoles = updatedRoles?.filter(updatedRole => updatedRole?.id !== role?.id);
    } else {
      updatedRoles.push({ id: role?.id, title: role?.title, wage: role?.wage });
    }
    setStaffState(prevState => ({ ...prevState, roles: updatedRoles }));
  }

  function handleAddNewRoles() {
    let updatedRoles = staffState?.roles
      ? [...staffState.roles]
      : [];

    updatedRoles = roleModal?.selectedRoles ? [...updatedRoles, ...roleModal.selectedRoles] : updatedRoles;
    setStaffState(prev => ({ ...prev, roles: updatedRoles }));
    void closeRoleModal();
  }

  function handleWageChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
    const { value } = e.target;
    const updatedRoles = staffState?.roles ? [...staffState.roles] : [];
    const updatedRole = { ...updatedRoles[index], wage: value };
    updatedRoles[index] = updatedRole;
    setStaffState(prev => ({ ...prev, roles: updatedRoles }));
  }

  return (
    <Page
      title={t("Create new staff account")}
      breadcrumbs={[
        { label: t("Settings") },
        { label: t("Staff Accounts"), url: "/admin/settings/staff" },
        { label: t("Create new staff account"), url: "/admin/settings/staff_new" },
      ]}
    >
      <Card title={t("Contact Information")}>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={staffState?.first_name}
                label={t("First Name")}
                id="first_name"
                onChange={handleInputChange}
                placeholder={t("First Name")}
                error={staffState?.first_name?.isDirty && !staffState?.first_name?.isValid}
              />
              <Input
                value={staffState?.last_name}
                label={t("Last Name")}
                id="last_name"
                onChange={handleInputChange}
                placeholder={t("Last Name")}
                error={staffState?.last_name?.isDirty && !staffState?.last_name?.isValid}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={staffState?.email}
                label={t("Email")}
                id="email"
                onChange={handleInputChange}
                placeholder={t("Email")}
                error={staffState?.email?.isDirty && !staffState?.email?.isValid}
              />
              <Input
                value={staffState?.username}
                label={t("Username")}
                id="username"
                onChange={handleInputChange}
                placeholder={t("Username")}
                disabled={staffState?.state === "disabled"}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={staffState?.password?.data}
                label={t("Password")}
                type="password"
                id="password"
                onChange={handleInputChange}
                placeholder={t("Password")}
                error={staffState?.password?.isDirty && !staffState?.password?.isValid}
                helpText={t("Password length must be at least 5 characters ")}
              />
              <Input
                value={quickCode}
                label={kioskViewEnabled ? t("PIN (Required)") : t("PIN (Optional)")}
                id="quick_code"
                onChange={handleInputChange}
                placeholder={t("i.e. 1234")}
                error={pinMissing}
                helpText={
                  kioskViewEnabled
                    ? t("A PIN is required because Kiosk View is enabled.")
                    : t("Staff can enter a PIN to access Point-of-Sale app.")
                }
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <Card
        title={t("Role & Access")}
        titleActions={
          accessMode === "role"
            ? [{ content: t("Add roles"), action: () => updateRoleModal({ isOpen: true, selectedRoles: [] }) }]
            : undefined
        }
      >
        <Card.Section>
          <div style={{ display: "flex", gap: 8, marginBottom: accessMode === "role" ? 16 : 0 }}>
            <ButtonNew type={accessMode === "role" ? "primary" : "secondary"} onClick={() => setAccessMode("role")}>
              {t("Assign a role")}
            </ButtonNew>
            <ButtonNew type={accessMode === "custom" ? "primary" : "secondary"} onClick={() => setAccessMode("custom")}>
              {t("Custom permissions")}
            </ButtonNew>
          </div>
          {accessMode === "role" ? (
            <DataTable columns={[{ label: t("Role"), width: "50%" }, ...(authStore?.user?.permissions?.admin_view_shift_wages ? [{ label: t("Wage"), width: "40%" }] : []), { label: "", width: "10%" }]}>
              {!staffState?.roles || staffState?.roles?.length === 0 ? <td colSpan={authStore?.user?.permissions?.admin_view_shift_wages ? 3 : 2}> <MessageDisplay title={t("No roles set")} content={t("There are no roles set on this account. Select 'Add roles' to add new roles to this account")} icon="magnifying-glass" /></td> : staffState?.roles
                ?.map((role, index) => {
                  return (
                    <tr key={index}>
                      <td>{role?.title}</td>
                      {authStore?.user?.permissions?.admin_view_shift_wages ? <td>
                        <Input onChange={e => handleWageChange(e, index)} labelHidden value={role?.wage ?? ""} prefix={facilityStore?.facility?.default_currency_symbol} suffix={t("/hr")} placeholder={"0.00"} /></td> : null}
                      <td> <FontAwesomeIcon
                        icon={faXmark}
                        className="cursor-pointer"
                        onClick={() => handleSelectRole(role)}
                      /></td>
                    </tr>
                  );
                })}
            </DataTable>
          ) : null}
        </Card.Section>
      </Card>
      {accessMode === "custom" ? (
        <PermissionEditor
          permissions={permissionCatalog}
          title={t("Permissions")}
          isChecked={permission => Boolean(customPermissions[permission.key])}
          onToggle={(permission: IPermissionDetails, checked) =>
            setCustomPermissions(prev => ({ ...prev, [permission.key]: checked }))
          }
          onToggleMany={(permissions, checked) =>
            setCustomPermissions(prev => {
              const next = { ...prev };
              permissions.forEach(permission => {
                next[permission.key] = checked;
              });
              return next;
            })
          }
          isRowDisabled={permission =>
            permission.key === "kiosk_view" && !quickCode && !customPermissions.kiosk_view
          }
          rowDescription={permission =>
            permission.key === "kiosk_view" && !quickCode && !customPermissions.kiosk_view
              ? t("A PIN is required before enabling Kiosk View.")
              : permission.description
          }
        />
      ) : null}
      <div className="flex flex-row justify-end pt-3">
        <ButtonNew type="text" onClick={() => history.push("/admin/settings/staff/")}>{t("Cancel")}</ButtonNew>
        <ButtonNew type="primary" onClick={() => createUser()} disabled={pinMissing}>
          {t("Create Staff Account")}
        </ButtonNew>
      </div>


      <Portal isMounted={roleModal?.isOpen}>
        <Sheet
          open={roleModal?.isOpen}
          title={t("Add roles")}
          size="small"
          onCancel={closeRoleModal}
          onOk={handleAddNewRoles}
          okText={t("Add")}
          okDisabled={roleModal?.selectedRoles?.length === 0}
        >
          <div className="ui-checkbox-group">
            {roleState?.filter(role => !staffState?.roles?.some(staffRole => staffRole?.id === role?.id))?.map((role, index) => {
              const checked = roleModal?.selectedRoles?.some(selectedRole => selectedRole?.id === role?.id);
              return (
                <div
                  key={index}
                  onClick={() => handleSelectNewRole(role)}
                  className={classNames("ui-checkbox-group-item", {
                    "ui-checkbox-group-item_selected": checked,
                  })}
                >
                  <div className="ui-checkbox-group-item-content">
                    <div className="ui-checkbox-group-item-text">
                      <p className="text-semibold">{role.title}</p>
                      {role?.wage && authStore?.user?.permissions?.admin_view_shift_wages ? <p><LocaleCurrency amount={role?.wage} currency={facilityStore?.facility?.default_currency_code} />{t("/hr")}</p> : null}
                    </div>
                  </div>
                  <Checkbox
                    size="medium"
                    checked={checked}
                    onChange={() => handleSelectNewRole(role)}
                  />
                </div>
              );
            })}
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}
